/**
 * ServiceInstallChecklist.jsx
 * Structural Controller Component Layer for Quality Engineering Installation Checklists
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './InstallChecklist.css';
import { api, todayStr, nowStr, recordAuditLog } from '../../api/rapid-imlementation-workflow-api';
import { currentRole, isTabAllowed, CHROME_TABS } from './roleAccess';

const MASTER_CHECKLIST_MODULES = [
  { id: "ctp", label: "CTP", delivery: "N/A", note: "Enable if contracted for Rapid4Angio/C-ARM" },
  { id: "mr", label: "MR", delivery: "N/A" },
  { id: "cta", label: "CTA", delivery: "N/A" },
  { id: "lvo", label: "LVO", delivery: "N/A" },
  { id: "aspects", label: "ASPECTS" },
  { id: "ich", label: "ICH", delivery: "N/A" },
  { id: "hyperdensity", label: "Hyperdensity", delivery: "N/A" },
  { id: "hypodensity", label: "Hypodensity NCCT", delivery: "N/A" },
  { id: "ncct", label: "NCCT Stroke", delivery: "N/A" },
  { id: "sdh", label: "SDH", delivery: "N/A" },
  { id: "mls", label: "MLS", delivery: "N/A" },
  { id: "petn", label: "PETN", delivery: "N/A" },
  { id: "rvlv", label: "RVLV", delivery: "N/A" },
  { id: "oh", label: "OH", delivery: "N/A" },
  { id: "vo", label: "VO+", delivery: "N/A", note: "Enabling LMVO requires disabling LVO - document in Section 6" },
  { id: "am", label: "AM", delivery: "N/A" },
  { id: "ipe", label: "iPE", delivery: "N/A" },
  { id: "anrtn", label: "ANRTN", delivery: "N/A" },
  { id: "lumina", label: "Lumina 3D", delivery: "N/A" },
  { id: "delta", label: "Delta Fuse", delivery: "N/A" },
  { id: "sp", label: "SP Integration", delivery: "N/A", note: "May be a two-stage process: install then connection." }
];

const QUALITY_VERIFY_STEPS = [
  { id: "ig", label: "Installation Guide document number, revision, and filename confirmed and on record" },
  { id: "testimaging", label: "Test imaging data received and processed successfully by each active module" },
  { id: "modactive", label: "All ordered modules verified as active and correctly configured" },
  { id: "dicom", label: "Target DICOM node verified - studies routing correctly" },
  { id: "email", label: "Email notifications verified for all active modules" },
  { id: "beacon", label: "Beacon and usage scripts verified executing without errors" },
  { id: "version", label: "Installed version confirmed and matches installed version recorded in Section 2" }
];

// Structural Lookup Translation Dictionary
const INTAKE_TO_CHECKLIST_MODULE = {
    mismatch_ctp: 'ctp',
    mismatch_mri: 'mr',
    cta: 'cta',
    lvo: 'lvo',
    aspects: 'aspects',
    ich: 'ich',
    hyperdensity: 'hyperdensity',
    hypodensity: 'hypodensity',
    ncctstroke: 'ncct',
    sdh: 'sdh',
    midline_shift: 'mls',
    pe_triage: 'petn',
    rv_lv: 'rvlv',
    obstr_hydro: 'oh',
    vo_plus: 'vo',
    aortic_meas: 'am',
    ipe: 'ipe',
    aneurysm: 'anrtn',
    lumina3d: 'lumina',
    deltafuse: 'delta',
};

const CORE_MODULE_MAP = {
  mismatch_ctp: "ctp", mismatch_mri: "mr", cta: "cta", lvo: "lvo", aspects: "aspects", ich: "ich",
  hyperdensity: "hyperdensity", hypodensity: "hypodensity", ncct_stroke: "ncct", sdh: "sdh", midline_shift: "mls",
  pe_triage: "petn", rv_lv: "rvlv", obstr_hydro: "oh", vo_plus: "vo", aortic_meas: "am", ipe: "ipe",
  aneurysm: "anrtn", lumina3d: "lumina", deltafuse: "delta"
};

const DRAFT_PILL_LABELS = { not_started: "Not started", in_progress: "In progress", ready_for_qa: "Ready for QA", complete: "Complete" };
const SERVER_INPUT_FIELDS = [
  { key: "serverUUID", label: "Server UUID" },
  { key: "clusterId", label: "Cluster ID" },
  { key: "macAddress", label: "MAC Address" },
  { key: "currentVersion", label: "Current Version" }
];

const safeSiteId = (value) => {
  const id = String(value || '').trim();
  return /^[a-zA-Z0-9_-]+$/.test(id) ? id : null;
};

export default function ServiceInstallChecklist({ onTabChange }) {
  const role = currentRole();

  // Database Scope Registers state definitions
  const [finalizedIntakes, setFinalizedIntakes] = useState([]);
  const [quoteQuery, setQuoteQuery] = useState('');
  //const [matchedIntakes, setMatchedIntakes] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState('');
  const [siteRecords, setSiteRecords] = useState({});
  const [lookupMessage, setLookupMessage] = useState('Loading checklist-ready case list...');
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [activeIntake, setActiveIntake] = useState(null);

  const saveTimer = useRef(null);

  // Core Data Prefill Translators
  const computeServiceType = (type) => type === "new_sale" ? "New Install" : ["addon_only", "renewal_addon", "scanner_addon"].includes(type) ? "Add Ons" : "";
  const computeLicenseType = (type) => type === "evaluation" ? "Commercial Evaluation" : type === "research" ? "Clinical/Research" : "";

  const mapContractedModules = useCallback((moduleIds) => {
    const mods = {};
    const unmapped = [];
    (moduleIds || []).forEach(id => {
      const target = CORE_MODULE_MAP[id];
      if (target) mods[target] = { ordered: true, installed: "", version: "", note: "" };
      else unmapped.push(id);
    });
    return { mods, unmapped };
  }, []);

  const createBlankChecklist = useCallback((prefill) => {
    const mapped = mapContractedModules(prefill.orderedModuleIds || []);
    return {
      serviceType: computeServiceType(prefill.contractType),
      licenseType: computeLicenseType(prefill.contractType),
      si: {
        site: prefill.siteName || prefill.parentAccount || prefill.opportunityName || "",
        caseNum: prefill.caseNumber || "", quoteRef: prefill.quoteNum || "", engineer: "", date: todayStr(),
        clusterId: "", serverUUID: "", macAddress: "", currentVersion: "", installedVersion: "", packageName: "",
        deviation: "N/A", deviationNotes: "", comments: "", unmappedModules: mapped.unmapped.join(", ")
      },
      mods: mapped.mods, steps: {}, stepNotes: {}, csSrc: "", csDst: "",
      snapshots: {}, fetchMessages: {}, fetchState: { preOpened: false, postOpened: false }, approval: null
    };
  }, [mapContractedModules]);

  const normalizeChecklistData = useCallback((record, prefill) => {
    const base = createBlankChecklist(prefill);
    const saved = record?.checklist || {};
    return {
      serviceType: saved.serviceType || base.serviceType,
      licenseType: saved.licenseType || base.licenseType,
      si: { ...base.si, ...(saved.si || {}) },
      mods: { ...base.mods, ...(saved.mods || {}) },
      steps: { ...(saved.steps || {}) },
      stepNotes: { ...(saved.stepNotes || {}) },
      csSrc: saved.csSrc || "", csDst: saved.csDst || "",
      snapshots: { ...(saved.snapshots || {}) },
      fetchMessages: { ...(saved.fetchMessages || {}) },
      fetchState: { ...base.fetchState, ...(saved.fetchState || {}) },
      approval: saved.approval || null
    };
  }, [createBlankChecklist]);

  const computeRecordStatus = (record) => {
    if (!record) return "not_started";
    if (record.status && record.status !== "not_started") return record.status;
    const c = record.checklist || {};
    const hasEdits = Object.keys(c.steps || {}).length || c.csSrc || c.csDst || c.si?.engineer;
    return hasEdits ? "in_progress" : "not_started";
  };

  // Lifecycle Initialization
  useEffect(() => {
    const initializePortal = async () => {
        try {
          await api.logPortalAccess('Install Checklist', '/');
        } catch (err) {
          console.warn('[Telemetry Core] Failed to register portal access event:', err.message);
        }
    };
    initializePortal();
    api.fetchFinalizedIntakes()
      .then(d => {
        const pool = d.intakes || [];
        setFinalizedIntakes(pool);
        setLookupMessage(pool.length ? `${pool.length} checklist-ready case(s) available.` : "No Partially Installed or Install Complete cases are available yet.");
      })
      .catch(e => setLookupMessage(`Initialization failure: ${e.message}`));
  }, []);

  const loadSiteChecklistRecord = useCallback(async (siteId, targetIntake) => {
    const stringSiteId = String(safeSiteId(siteId) || siteId);
    try {
      const data = await api.getChecklistRecord(targetIntake.quoteNum, stringSiteId, targetIntake.id);
      const prefill = data.prefill || { quoteNum: targetIntake.quoteNum, intakeId: targetIntake.id, orderedModuleIds: [] };
      
      setSiteRecords(prev => {
        const record = {
          quoteNum: prefill.quoteNum || targetIntake.quoteNum, 
          intakeId: prefill.intakeId || targetIntake.id, 
          siteId: stringSiteId,
          status: data.record?.status || "not_started", 
          prefill: {
            ...prefill,
            siteId: stringSiteId
          },
          checklist: normalizeChecklistData(data.record, prefill)
        };
        record.status = data.record?.status || computeRecordStatus(record);
        
        if (!window.AppRecordStore) window.AppRecordStore = {};
        window.AppRecordStore[stringSiteId] = record;
        
        return { ...prev, [stringSiteId]: record };
      });
    } catch (e) {
      console.error(`Failed loading record profile for site context ${stringSiteId}`, e);
    }
  }, [normalizeChecklistData]);

  const handleCaseSelection = async (quoteNum, preferredIntakeId = '') => {
    setQuoteQuery(quoteNum);
    if (!quoteNum) {
      /*setMatchedIntakes([]);*/ setActiveIntake(null); setActiveSiteId(''); setSiteRecords({});
      return;
    }
    setLoading(true);
    setLookupMessage("Looking up checklist-ready case profiles...");
    try {
      const data = await api.lookupIntakeMatches(quoteNum);
      const matches = data.matches || [];
      //setMatchedIntakes(matches);
      // When arriving from the Queue we know the exact intake id; prefer it so the
      // case opens automatically even if the quote has more than one match.
      const preferred = preferredIntakeId ? matches.find(m => m.id === preferredIntakeId) : null;
      if (preferred || matches.length === 1) {
        const primary = preferred || matches[0];
        setActiveIntake(primary);
        setLookupMessage(`Loaded case ${primary.quoteNum}. Select active target site profile.`);
        await Promise.all((primary.sites || []).map(s => loadSiteChecklistRecord(s.id, primary)));
      } else {
        setLookupMessage(`${matches.length} matches linked. Choose accurate operational target context allocation.`);
      }
    } catch (e) {
      setLookupMessage(`Execution match lookup mapping routing trace error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load the case when arriving from the Queue's "Open checklist" hand-off
  // (URL carries ?quoteNum=&intakeId=&tab=checklist). Runs once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlQuote = params.get('quoteNum') || '';
    const urlIntakeId = params.get('intakeId') || '';
    if (urlQuote) {
      handleCaseSelection(urlQuote, urlIntakeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentRecord = siteRecords[activeSiteId] || null;
  const currentSite = activeIntake?.sites?.find(s => String(safeSiteId(s.id) || s.id) === String(activeSiteId)) || null;

  // Client-side audit trail for checklist work (the table previously showed no
  // checklist step activity at all). Fire-and-forget — never blocks the UI.
  const logChecklistEvent = (action, detail) => {
    recordAuditLog({
      category: 'checklist',
      action,
      detail,
      target: currentRecord?.checklist?.si?.site || currentSite?.name || '',
      quoteNum: activeIntake?.quoteNum || currentRecord?.quoteNum || '',
      intakeId: activeIntake?.id || currentRecord?.intakeId || '',
      siteId: activeSiteId || '',
    }).catch(() => {});
  };

  // Step-note values captured on focus so blur can log only real changes
  // (logging per keystroke would flood the audit table).
  const stepNoteFocusValues = useRef({});

  // Persisted Database Update Logic
  const commitChecklistToDatabase = useCallback(async (showNotification, statusOverride = null) => {
    if (!activeIntake || !currentRecord) return false;
    clearTimeout(saveTimer.current);
    
    const targetStatus = statusOverride || computeRecordStatus(currentRecord);
    currentRecord.status = targetStatus;
    
    if (showNotification) setSaveMessage("Saving structural tracking matrices...");
    try {
      // notifySave tells the lambda this is an explicit user save (vs the 1s
      // autosave) so the server-side audit backstop doesn't flood the log.
      await api.updateChecklistRecord(activeIntake.quoteNum, currentRecord.siteId, activeIntake.id, {
        status: targetStatus, checklist: currentRecord.checklist, notifySave: showNotification === true
      });
      setSaveMessage(`Saved at ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`);
      if (showNotification === true) {
        logChecklistEvent('Saved install checklist', `Saved install checklist (status: ${targetStatus}).`);
      }
      return true;
    } catch (e) {
      setSaveMessage(`Save tracing failure: ${e.message}`);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIntake, currentRecord]);

  const scheduleAutosaveCheckpoint = useCallback(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (currentRecord && currentRecord.status === 'not_started') {
        currentRecord.status = 'in_progress';
      }
      commitChecklistToDatabase(false);
    }, 1000);
  }, [currentRecord, commitChecklistToDatabase]);

  const handleFieldMutation = (key, value) => {
    if (!currentRecord) return;
    setSiteRecords(prev => {
      const next = { ...prev };
      next[activeSiteId].checklist.si[key] = value;
      return next;
    });
    scheduleAutosaveCheckpoint();
  };

  const handleGenericChecklistMutation = (updateFn) => {
    if (!currentRecord) return;
    setSiteRecords(prev => {
      const next = { ...prev };
      updateFn(next[activeSiteId].checklist);
      return next;
    });
    scheduleAutosaveCheckpoint();
  };

  // Post-install module fields live under snapshots.post.modules[<id>] — read AND
  // write the same key so edits survive a reload (previously version/note were
  // written here but read from checklist.mods[label], which silently lost data).
  const mutatePostModule = (modId, field, value) => handleGenericChecklistMutation(c => {
    if (!c.snapshots) c.snapshots = {};
    if (!c.snapshots.post) c.snapshots.post = { found: false, server: {}, modules: {} };
    if (!c.snapshots.post.modules) c.snapshots.post.modules = {};
    if (!c.snapshots.post.modules[modId]) c.snapshots.post.modules[modId] = { id: modId };
    c.snapshots.post.modules[modId][field] = value;
  });

  const openSite = async (siteId) => {
    const stringSiteId = String(safeSiteId(siteId) || siteId);
    if (activeSiteId && activeSiteId !== stringSiteId) {
      await commitChecklistToDatabase(false);
    }
    if (!siteRecords[stringSiteId] && activeIntake) {
      await loadSiteChecklistRecord(stringSiteId, activeIntake);
    }
    setActiveSiteId(stringSiteId);
    setSaveMessage("");
    setTimeout(() => {
      document.getElementById("editorCard")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleNextSiteShift = async () => {
    const sites = activeIntake?.sites || [];
    const idx = sites.findIndex(s => String(safeSiteId(s.id) || s.id) === String(activeSiteId));
    const next = sites[idx + 1];
    if (next) await openSite(next.id);
  };

//   const triggerDiagnosticTelemetryFetch = async (phase) => {
//     if (!activeIntake || !currentRecord) return;
//     await commitChecklistToDatabase(false);
    
//     handleGenericChecklistMutation(c => {
//       c.fetchState[phase === "post" ? "postOpened" : "preOpened"] = true;
//       c.fetchMessages[phase] = `Fetching baseline structural diagnostics ${phase}-install parameters context data stream...`;
//     });

//     try {
//         const masterQuery = currentRecord.prefill?.parentAccount || "";
//         const subQuery = currentRecord.prefill?.siteName || "";
        
//         const dbMatch = await api.getSiteInfo(masterQuery, subQuery);
//       const data = await api.fetchSiteDiagnosticsInfo(
//         dbMatch?.projectId, dbMatch?.clusterId, dbMatch?.subSiteCode);
//       const siteInfo = data || {};
      
//       handleGenericChecklistMutation(c => {
//         c.snapshots[phase] = siteInfo;
//         c.fetchMessages[phase] = siteInfo.message || (siteInfo.found ? "Telemetry metrics populated." : "Telemetry index unmapped. Input data components manually.");
        
//         if (phase === "pre" && siteInfo.found) {
//           const incomingServer = siteInfo.server || {};
//           SERVER_INPUT_FIELDS.forEach(f => { if (incomingServer[f.key]) c.si[f.key] = incomingServer[f.key]; });
//           Object.entries(siteInfo.modules || {}).forEach(([id, inbound]) => {
//             c.mods[id] = { ...(c.mods[id] || {}), ordered: true, installed: inbound.installed || "", version: inbound.version || "" };
//           });
//         } else if (phase === "post") {
//           c.snapshots.post = {
//             found: true, source: siteInfo.source || "manual-entry", fetchedAt: new Date().toISOString(),
//             server: siteInfo.server || {}, modules: siteInfo.modules || {}
//           };
//         }
//       });
//     } catch (e) {
//       handleGenericChecklistMutation(c => { c.fetchMessages[phase] = `Diagnostics trace abort connection error: ${e.message}`; });
//     }
//   };

    const triggerDiagnosticTelemetryFetch = async (phase) => {
    if (!activeIntake || !currentRecord) return;
    await commitChecklistToDatabase(false);
    
    handleGenericChecklistMutation(c => {
      c.fetchState[phase === "post" ? "postOpened" : "preOpened"] = true;
      c.fetchMessages[phase] = `Fetching server information as part of ${phase}-install checklist...`;
    });
    logChecklistEvent('Requested site info fetch', `Started ${phase}-install server info fetch.`);

    try {
      const masterQuery = currentRecord.prefill?.parentAccount || "";
      const subQuery = currentRecord.prefill?.siteName || "";
      
      // 1. Unpack the responseData wrapper returned by your backend routing engine
      const siteConfigRes = await api.getSiteInfo(masterQuery, subQuery);
      const siteConfig = siteConfigRes || {};
      
      if (!siteConfig.found) {
        throw new Error(siteConfigRes?.error || "No dynamic deployment markers found for this facility registry mapping.");
      }

      // 2. Dispatch parameters into your health-check engine metrics pipeline
      const data = await api.fetchSiteDiagnosticsInfo(
        siteConfig.projectId, 
        siteConfig.clusterId, 
        siteConfig.subSiteCode
      );
      
      // Mirror the original index structural behavior perfectly
      var siteInfo = data || {};
      const isSuccessfullyFound = siteInfo.found === true;
      siteInfo = siteInfo?.data || {}
      
      handleGenericChecklistMutation(c => {
        c.snapshots[phase] = siteInfo;
        
        // 3. Evaluate the injected found flag safely at the root block level
      
        
        c.fetchMessages[phase] = siteInfo.message || (isSuccessfullyFound ? "Server Information fetched successfully." : "Failed to fetch server information. Please fill the below fields manually.");
        
        if (phase === "pre" && isSuccessfullyFound) {
            const incomingServer = siteInfo.server || {};
            
            // Hydrate Section 2 Server Input Fields
            SERVER_INPUT_FIELDS.forEach(f => { 
                if (incomingServer[f.key]) {
                c.si[f.key] = incomingServer[f.key]; 
                }
            });

            // Convert incoming payload dictionary keys or array objects safely using the 1:1 original mapping rules
            const normalizedIncomingModules = {};
            
            // Accept both dictionary objects or array collections from the response layer
            const rawEntries = typeof siteInfo.modules === 'object' && siteInfo.modules !== null
                ? Object.entries(siteInfo.modules)
                : [];

            rawEntries.forEach(([key, item]) => {
                if (!item) return;

                // Fallback resolution path matching the original normalizeChecklistModuleId backend rules
                const rawValue = item.id || item.moduleId || item.module_id || item.name || item.label || key;
                const cleanServerLabel = String(rawValue || '').trim().toLowerCase();

                // Convert the normalized label string back to the short layout ID using the translation dictionary
                const targetId = INTAKE_TO_CHECKLIST_MODULE[cleanServerLabel] || cleanServerLabel;
                
                if (targetId) {
                normalizedIncomingModules[targetId] = item;
                }
            });

            // Iterate across every single possible module in the master catalog to refresh the UI state
            MASTER_CHECKLIST_MODULES.forEach(m => {
                const inbound = normalizedIncomingModules[m.id];
                
                // Initialize default fallback values first for complete structural safety
                let isOrdered = false;
                let isInstalled = "";
                let versionStr = "";
                let resolvedDelivery = "N/A";

                if (inbound) {
                isOrdered = true;
                isInstalled = String(inbound.installed || inbound.status || '').trim() === 'true' ? 'Yes' : 'No';
                versionStr = inbound.version || inbound.installedVersion || inbound.currentVersion || "";
                
                const rawDeliveryValue = inbound.deliveryType || inbound.delivery_type || inbound.delivery || '';
                const serverDeliveryType = String(rawDeliveryValue).trim().toLowerCase();
                
                resolvedDelivery = serverDeliveryType === 'cloud' ? 'Cloud' : 'Hybrid';
                }

                // ── FIX: BREAK OBJECT REFERENCE FORCING REACT UI RE-RENDER ──
                c.mods = {
                ...c.mods, // Creates a brand new container block reference
                [m.id]: {
                    ...(c.mods[m.id] || {}), // Spreads old configurations safely
                    ordered: isOrdered,
                    installed: isInstalled,
                    version: versionStr,
                    delivery: resolvedDelivery // Injects the newly captured value cleanly
                }
                };
            });
        } else if (phase === "post") {
    const incomingServer = siteInfo.server || {};

    // Hydrate Section 2 Server Input Fields
    SERVER_INPUT_FIELDS.forEach(f => { 
        if (incomingServer[f.key]) {
            c.si[f.key] = incomingServer[f.key]; 
        }
    });

    // 1. Normalize the baseline authorization array of prefilled ordered module IDs
    const rawPrefillArray = currentRecord.prefill?.orderedModuleIds || [];
    const cleanPrefillArray = typeof rawPrefillArray === "string" 
        ? rawPrefillArray.split(",") 
        : rawPrefillArray;

    // Translate prefill strings (labels or raw IDs) into standard short checklist IDs
    const targetChecklistIds = cleanPrefillArray.map(id => {
        const cleanId = String(id || '').trim().toLowerCase();
        return INTAKE_TO_CHECKLIST_MODULE[cleanId] || cleanId;
    });

    // 2. Normalize incoming telemetry server data structures
    const normalizedIncomingModules = {};
    const rawEntries = typeof siteInfo.modules === 'object' && siteInfo.modules !== null
        ? Object.entries(siteInfo.modules)
        : [];

    rawEntries.forEach(([key, item]) => {
        if (!item) return;
        const rawValue = item.id || item.moduleId || item.module_id || item.name || item.label || key;
        const cleanServerLabel = String(rawValue || '').trim().toLowerCase();
        const targetId = INTAKE_TO_CHECKLIST_MODULE[cleanServerLabel] || cleanServerLabel;
        
        if (targetId) {
            normalizedIncomingModules[targetId] = item;
        }
    });

    // 3. Sweep the MASTER catalog and write the normalized rows straight into the
    // post snapshot (snapshots.post.modules[<id>]) — the single source of truth the
    // Section 7 table, exports, and PDFs all read from.
    const postModules = {};
    MASTER_CHECKLIST_MODULES.forEach(m => {
        // --- ADDED INTAKE MAPPING LAYER LOGIC HERE TO MATCH PRE PHASE ---
        const cleanMasterId = String(m.id || '').trim().toLowerCase();
        const resolvedTargetId = INTAKE_TO_CHECKLIST_MODULE[cleanMasterId] || cleanMasterId;

        // A module is marked 'ordered' ONLY if its resolved ID is explicitly present in the prefill array
        const isOrderedByPrefill = targetChecklistIds.includes(resolvedTargetId);

        // Use the resolved target ID to correctly index into our normalized incoming server telemetry map
        const inbound = normalizedIncomingModules[resolvedTargetId];

        // Extract telemetry values if a matching server object is available
        const isInstalled = inbound ? (String(inbound.installed || inbound.status || '').trim() === 'true' ? 'Yes' : 'No') : "";
        const versionStr = inbound ? (inbound.version || inbound.installedVersion || inbound.currentVersion || "") : "";
        const noteStr = inbound ? (inbound.note || inbound.notes || "") : "";

        // Delivery logic remains identical to the pre-install phase
        const rawDeliveryValue = inbound ? (inbound.deliveryType || inbound.delivery_type || inbound.delivery || '') : '';
        const serverDeliveryType = String(rawDeliveryValue).trim().toLowerCase();
        const resolvedDelivery = serverDeliveryType === 'cloud' ? 'Cloud' : 'Hybrid';

        postModules[m.id] = {
            ...(c.snapshots?.post?.modules?.[m.id] || {}),
            id: m.id,
            label: m.label,
            ordered: isOrderedByPrefill,
            installed: isInstalled,
            version: versionStr,
            note: noteStr,
            delivery: resolvedDelivery
        };
    });

    // Persist the rebuilt post snapshot (server + normalized modules)
    c.snapshots.post = {
        found: true,
        source: siteInfo.source || "automated-telemetry",
        fetchedAt: new Date().toISOString(),
        server: incomingServer,
        modules: postModules
    };
}
      });
      logChecklistEvent('Fetched site info', `${phase}-install server info ${isSuccessfullyFound ? 'fetched successfully' : 'not found — fields require manual entry'}.`);
    } catch (e) {
      handleGenericChecklistMutation(c => {
        c.fetchMessages[phase] = `Exception occurred when fetching server information: ${e.message}`;
      });
      logChecklistEvent('Site info fetch failed', `${phase}-install server info fetch failed: ${e.message}`);
    }
  };

  const downloadChecklistExcelReport = (phase) => {
    if (typeof window.XLSX === 'undefined') {
      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
      script.onload = () => downloadChecklistExcelReport(phase);
      document.head.appendChild(script);
      return;
    }

    const record = window.AppRecordStore?.[activeSiteId];
    if (!record) return;

    const exportStamp = new Date().toISOString().replace(/[:.]/g, "-");
    const worksheetTitle = phase === "pre" ? "Pre-Install Checklist" : "Post-Install Checklist";
    
    const sheetData = [
      ["SERVER AND SITE RECONSTRUCT MATRIX METADATA REPORT"],
      ["Generated timestamp:", nowStr()],
      ["Phase Context Reference:", worksheetTitle],
      ["System Parent Account Identifier:", record.prefill?.parentAccount || "N/A"],
      ["Child Target Facility Destination:", record.checklist?.si?.site || "N/A"],
      ["Case Assignment Token:", record.checklist?.si?.caseNum || "N/A"],
      ["Orchestration Cluster Reference UUID:", record.checklist?.si?.clusterId || "N/A"],
      [],
      ["FACILITY ACTIVE PRODUCT SELECTION CONFIGURATIONS"],
      ["Module Signature Code", "Operational Verification State", "Delivery Model", "Deployment Metadata Version"]
    ];

    const sourceModules = phase === "post" ? (record.checklist?.snapshots?.post?.modules || {}) : (record.checklist?.mods || {});

    MASTER_CHECKLIST_MODULES.forEach(m => {
      const liveInstance = sourceModules[m.id] || {};
      sheetData.push([
        m.label,
        liveInstance.installed ? "Yes" : "No",
        liveInstance.delivery || m.delivery || "Standard Premise",
        liveInstance.version || "N/A"
      ]);
    });

    const wb = window.XLSX.utils.book_new();
    const ws = window.XLSX.utils.aoa_to_sheet(sheetData);
    window.XLSX.utils.book_append_sheet(wb, ws, "Audit Checklist Telemetry");
    window.XLSX.writeFile(wb, `${phase.toUpperCase()}_Site_Checklist_Export_${exportStamp}.xlsx`);
  };

  const handleChecklistVerificationFinalApproval = async () => {
    if (!activeIntake || !currentRecord) return;
    setApproving(true);
    
    const updatedChecklist = { ...currentRecord.checklist, approval: { status: "approved", verifiedBy: currentRecord.checklist.si?.engineer || "QA Principal", verifiedAt: new Date().toISOString() } };
    currentRecord.checklist = updatedChecklist;

    const success = await commitChecklistToDatabase(true, "complete");
    if (success) {
      logChecklistEvent('Approved install checklist', `Verified by ${updatedChecklist.approval.verifiedBy} at ${updatedChecklist.approval.verifiedAt}.`);
      // Hand off to the Install-Complete view, which fires the Salesforce update and
      // exposes the (now real) Pre/Post/Delta PDF bundle download.
      window.history.replaceState(null, '', `/?quoteNum=${encodeURIComponent(activeIntake.quoteNum)}&siteId=${encodeURIComponent(currentRecord.siteId)}&intakeId=${encodeURIComponent(activeIntake.id)}&tab=install-complete`);
      onTabChange('install-complete');
    }
    setApproving(false);
  };

  const renderStatusPill = (status) => {
    const colorClass = status === "complete" ? "green" : status === "ready_for_qa" ? "blue" : status === "in_progress" ? "amber" : "";
    return <span className={`pill ${colorClass}`}>{DRAFT_PILL_LABELS[status] || DRAFT_PILL_LABELS.not_started}</span>;
  };

  const isPreOpen = currentRecord ? Boolean(currentRecord.checklist?.fetchState?.preOpened || currentRecord.checklist?.snapshots?.pre) : false;
  const isPostOpen = currentRecord ? Boolean(currentRecord.checklist?.fetchState?.postOpened || currentRecord.checklist?.snapshots?.post) : false;
  const siteListPool = activeIntake?.sites || [];
  const currentActiveIndex = siteListPool.findIndex(s => String(safeSiteId(s.id) || s.id) === String(activeSiteId));

  const preInstalledCount = currentRecord ? Object.values(currentRecord.checklist?.mods || {}).filter(m => m.installed).length : 0;
  const postOrderedCount = currentRecord ? Object.values(currentRecord.checklist?.snapshots?.post?.modules || {}).filter(m => m.installed === 'Yes' || m.installed === 'true').length : 0;

  return (
    <div className="checklist-wrapper">
      {/* CHROME */}
      <div id="chrome" className="no-print">
        <div className="chrome-logo"><span>SF</span> Sales Cloud</div>
        <div className="chrome-nav">
          {CHROME_TABS.filter(t => isTabAllowed(role, t.id)).map(t => (
            <button key={t.id} type="button" className={`chrome-nav-item ${t.id === 'checklist' ? 'active' : ''}`} onClick={() => onTabChange(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="chrome-crumb">
          <span>Dashboard</span><span className="chrome-sep">/</span><b>Service Install Checklist</b>
        </div>
        <div className="chrome-actions">
          <button className="sf-btn" onClick={() => commitChecklistToDatabase(true)}>Save checklist</button>
        </div>
      </div>

      <main className="shell">
        <div className="hero">
          <div>
            <h1>Service install checklist</h1>
            <p>Look up a Partially Installed or Install Complete case, pick an installed site, and complete the service installation checklist.</p>
          </div>
          <span className="pill blue">Installed sites only after validation</span>
        </div>

        {/* LOOKUP MATRIX INTERFACE BLOCK */}
        <section className="card lookup-card no-print">
          <div className="card-hd">
            <div><h2>Case lookup</h2><p>Only Partially Installed and Install Complete cases are available. Partially Installed cases show only validated installed sites.</p></div>
          </div>
          <div className="card-bd">
            <div className="field">
              <label>Checklist-ready cases</label>
              <select value={quoteQuery} onChange={e => handleCaseSelection(e.target.value)} disabled={loading}>
                <option value="">Select saved case...</option>
                {finalizedIntakes.map(item => (
                  <option key={item.id} value={item.quoteNum}>
                    {item.quoteNum} - {item.opportunityName || "Untitled Assignment Layout"} ({item.siteCount} sites)
                  </option>
                ))}
              </select>
              <div className="hint">Selecting a case loads its installed sites automatically.</div>
            </div>
            <div className={`alert ${finalizedIntakes && finalizedIntakes.length > 0 ? 'blue' : 'red'}`}> {lookupMessage} </div>
          </div>
        </section>

        {/* ACTIVE CASE RESOURCE CONTEXT PROFILE */}
        {activeIntake && (
          <section className="card site-list no-print">
            <div className="card-hd">
              <div><h2>Installed facilities execution checklist paths</h2><p>Select target deployment node parameter grid location profile to allocate checklist tracking elements.</p></div>
            </div>
            <div className="card-bd">
              <div className="site-grid">
                {(activeIntake.sites || []).map(s => {
                  const recordInstance = siteRecords[s.id];
                  const isCurrentlyOpen = String(activeSiteId) === String(s.id);
                  const status = recordInstance?.status || "not_started";
                  return (
                    <div key={s.id} className={`site-card ${isCurrentlyOpen ? 'active' : ''}`}>
                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div className="site-title">{s.name || "Unnamed Facility Target Node"}</div>
                          <div className="site-meta"><span>{s.type}</span><span>{s.region}</span></div>
                        </div>
                        {renderStatusPill(status)}
                      </div>
                      <button type="button" className={`btn ${isCurrentlyOpen ? 'primary' : ''}`} onClick={() => openSite(s.id)}>
                        {isCurrentlyOpen ? "Checklist open" : "Open Checklist"}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* DIAGNOSTIC COMPONENT GATEWAY WRAPPER */}
              {activeSiteId && (!currentRecord || !currentSite) && (
                <div className="alert red" style={{ marginTop: '15px', fontFamily: 'monospace', fontSize: '11px', lineHeight: '1.4' }}>
                  <strong>[Workspace Diagnostics Error]:</strong> Conditional Gate Locked.<br />
                  - Selected Site ID: "{activeSiteId}" (Type: {typeof activeSiteId})<br />
                  - Record Cache Found: {currentRecord ? "✅ Yes" : "❌ No (siteRecords key mismatch)"}<br />
                  - Site Object Found: {currentSite ? "✅ Yes" : "❌ No (activeIntake.sites match failure)"}
                </div>
              )}
            </div>
          </section>
        )}

        {/* COMPREHENSIVE WORKFLOW METRICS TERMINAL EDITOR WRAPPER */}
        {currentRecord && currentSite && (
          <div className="card" id="editorCard">
            <div className="card-hd">
              <div><h2>{currentSite.name} Deployment Worksheet Parameters</h2><p>Active Schema Reference Framework Blueprint Matrix Token</p></div>
              {renderStatusPill(currentRecord.status)}
            </div>
            <div className="card-bd">
              {/* SECTION 1 - ORDER REFERENCE */}
              <div className="section-title"><h3>Section 1 - Order Reference</h3><span>Quote + site</span></div>
              <div className="section-body">
                <div className="grid2">
                  <div className="field">
                    <label>Account / Site Name Identifier <span className="req">*</span></label>
                    <input type="text" value={currentRecord.checklist.si.site} onChange={e => handleFieldMutation('site', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Salesforce Case Reference Track Token <span className="req">*</span></label>
                    <input type="text" value={currentRecord.checklist.si.caseNum} onChange={e => handleFieldMutation('caseNum', e.target.value)} />
                  </div>
                </div>
                <div className="grid2">
                  <div className="field">
                    <label>Sales Quote / Contract Reference</label>
                    <input type="text" value={currentRecord.checklist.si.quoteRef} onChange={e => handleFieldMutation('quoteRef', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Deviation # (N/A if none)</label>
                    <input type="text" value={currentRecord.checklist.si.deviation} onChange={e => handleFieldMutation('deviation', e.target.value)} />
                  </div>
                </div>
                <div className="grid3">
                  <div className="field">
                    <label>Service Type Blueprint Context Selection</label>
                    <div className="seg-group">
                      {["New Install", "Upgrade", "Migration", "Add Ons"].map(t => (
                        <button key={t} type="button" className={`seg ${currentRecord.checklist.serviceType === t ? 'active' : ''}`} onClick={() => handleGenericChecklistMutation(c => { c.serviceType = t; })}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div className="field">
                    <label>License Authorization Target Schema</label>
                    <div className="seg-group">
                      {["Commercial", "Commercial Evaluation", "Clinical/Research", "ETAP"].map(l => (
                        <button key={l} type="button" className={`seg ${currentRecord.checklist.licenseType === l ? 'active' : ''}`} onClick={() => handleGenericChecklistMutation(c => { c.licenseType = l; })}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="field">
                    <label>Execution Date Track Stamp</label>
                    <input type="date" value={currentRecord.checklist.si.date} onChange={e => handleFieldMutation('date', e.target.value)} />
                  </div>
                </div>
                <div className="field" style={{ marginTop: '10px' }}>
                  <label>Authorized Installation Quality Field Engineer Signature Name <span className="req">*</span></label>
                  <input type="text" value={currentRecord.checklist.si.engineer} onChange={e => handleFieldMutation('engineer', e.target.value)} placeholder="Type full identity tracking string context..." />
                </div>
              </div>

              {/* TELEMETRY ENGINE CONTROL HOOKS DIAGNOSTICS LAYER */}
              <div className="row no-print" style={{ justifyContent: 'center', margin: '20px 0 12px' }}>
                <button type="button" className="btn primary" onClick={() => triggerDiagnosticTelemetryFetch('pre')}>Fetch pre-installed site info</button>
              </div>
              {currentRecord.checklist.fetchMessages.pre && (() => {
                    const msg = currentRecord.checklist.fetchMessages.pre.toLowerCase();
                    
                    // 1. Determine alert color based on message contents
                    let alertClass = "alert blue"; // Default fallback
                    
                    if (msg.includes("fetching") || msg.includes("sending")) {
                        alertClass = "alert amber";
                    } else if (msg.includes("fetched") || msg.includes("successfully")) {
                        alertClass = "alert green";
                    } else if (msg.includes("exception") || msg.includes("abort") || msg.includes("failed")) {
                        alertClass = "alert amber";
                    }

                    // 2. Render the alert element with the dynamically assigned class
                    return (
                        <div className={alertClass}>
                        {currentRecord.checklist.fetchMessages.pre}
                        </div>
                    );
                    })()}

              {/* SECTION 2 - SERVER / ENVIRONMENT */}
              {isPreOpen && (
                <>
                  <div className="section-title"><h3>Section 2 - Server / Environment</h3><span>Pre-install</span></div>
                  <div className="section-body">
                    <div className="grid2">
                      {SERVER_INPUT_FIELDS.map(f => (
                        <div key={f.key} className="field">
                          <label>{f.label}</label>
                          <input type="text" value={currentRecord.checklist.si[f.key] || ''} onChange={e => handleFieldMutation(f.key, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 3 - EXISTING MODULES CHECKLIST (skipped for New Install) */}
                  {currentRecord.checklist.serviceType !== 'New Install' && (
                  <>
                  <div className="section-title">
                    <h3>Section 3 - Existing Modules Checklist</h3>
                    <span>{preInstalledCount} installed</span>
                    <button type="button" className="btn primary" style={{ background: 'var(--purple)', border: 'none' }} onClick={() => downloadChecklistExcelReport('pre')}>Export Pre-Install Report</button>
                  </div>
                  <div className="section-body" style={{ overflowX: "auto" }}>
                    <table>
                      <thead>
                        <tr><th>Module</th><th style={{ textAlign: "center", width: "80px" }}>Install Type</th><th style={{ textAlign: "center", width: "75px" }}>Installed</th></tr>
                      </thead>
                      <tbody>
                        {MASTER_CHECKLIST_MODULES.map(m => {
                          const stateRef = currentRecord.checklist.mods[m.id] || { ordered: false };
                          return (
                            <tr key={m.id}>
                              <td><strong>{m.label}</strong>{m.note && <div className="hint">{m.note}</div>}</td>
                              <td style={{ textAlign: "center" }}><span className="pill">{stateRef.delivery|| "N/A"}</span></td>
                              <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!stateRef.ordered} className="readonly-box" readOnly /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                  )}
                </>
              )}

              {/* SECTION 4 - SOFTWARE INTEGRITY VERIFICATION */}
              <div className="section-title"><h3>Section 4 - Software Integrity Verification</h3><span>SHA-256</span></div>
              <div className="section-body">
                <div className={`cs-box ${currentRecord.checklist.csSrc && currentRecord.checklist.csSrc === currentRecord.checklist.csDst ? 'match' : currentRecord.checklist.csSrc && currentRecord.checklist.csDst ? 'mismatch' : ''}`}>
                  <div className="grid2">
                    <div className="field">
                      <label>Source Checksum (SHA-256)</label>
                      <input type="text" className="mono" value={currentRecord.checklist.csSrc} onChange={e => handleGenericChecklistMutation(c => { c.csSrc = e.target.value; })} />
                    </div>
                    <div className="field">
                      <label>Destination Checksum (SHA-256)</label>
                      <input type="text" className="mono" value={currentRecord.checklist.csDst} onChange={e => handleGenericChecklistMutation(c => { c.csDst = e.target.value; })} />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 5 - FUNCTIONAL VERIFICATION */}
              <div className="section-title"><h3>Section 5 - Functional Verification</h3><span>Manual</span></div>
              <div className="section-body">
                <table>
                  <thead>
                    <tr><th>Verification Step</th><th style={{ textAlign: "center", width: "160px" }}>Yes / No / N/A</th><th>Notes</th></tr>
                  </thead>
                  <tbody>
                    {QUALITY_VERIFY_STEPS.map(step => {
                      const currentVal = currentRecord.checklist.steps[step.id] || '';
                      return (
                        <tr key={step.id}>
                          <td>{step.label}</td>
                          <td>
                            <div className="tri">
                              {["Yes", "No", "N/A"].map(opt => (
                                <button key={opt} type="button" className={`${opt === 'Yes' ? 'yes' : opt === 'No' ? 'no' : 'na'} ${currentVal === opt ? 'active' : ''}`} onClick={() => {
                                  const next = currentVal === opt ? '' : opt;
                                  handleGenericChecklistMutation(c => { c.steps[step.id] = next; });
                                  logChecklistEvent('Set checklist verification step', `"${step.label}" → ${next || 'cleared'}.`);
                                }}>{opt}</button>
                              ))}
                            </div>
                          </td>
                          <td>
                            <input type="text" value={currentRecord.checklist.stepNotes[step.id] || ''} onChange={e => {
                              const v = e.target.value; handleGenericChecklistMutation(c => { c.stepNotes[step.id] = v; });
                            }}
                            onFocus={e => { stepNoteFocusValues.current[step.id] = e.target.value; }}
                            onBlur={e => {
                              if (stepNoteFocusValues.current[step.id] !== e.target.value) {
                                logChecklistEvent('Edited checklist step note', `"${step.label}" note → "${e.target.value}".`);
                              }
                              delete stepNoteFocusValues.current[step.id];
                            }} placeholder="Notes..." />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* SECTION 6 - DEVIATIONS AND COMMENTS */}
              <div className="section-title"><h3>Section 6 - Deviations and Comments</h3><span>Manual</span></div>
              <div className="section-body">
                <div className="field">
                  <label>Deviation Details</label>
                  <textarea value={currentRecord.checklist.si.deviationNotes || ''} placeholder="Required if Deviation # entered above" onChange={e => handleFieldMutation('deviationNotes', e.target.value)} />
                </div>
                <div className="field">
                  <label>Additional Comments</label>
                  <textarea value={currentRecord.checklist.si.comments || ''} placeholder="Any additional notes for QA review" onChange={e => handleFieldMutation('comments', e.target.value)} />
                </div>
              </div>

              {/* SECTION 7 - POST-INSTALL SITE INFO */}
              <div className="section-title">
                <h3>Section 7 - Post-install Site Info</h3>
                <span>{isPostOpen ? `${postOrderedCount} post-install` : "Collapsed"}</span>
                <button type="button" className="btn primary" style={{ background: 'var(--purple)', border: 'none' }} onClick={() => downloadChecklistExcelReport('post')}>Export Post-Install Report</button>
              </div>
              <div className="section-body">
                <div className="row no-print" style={{ justifyContent: 'center', marginBottom: '12px' }}>
                  <button type="button" className="btn primary" onClick={() => triggerDiagnosticTelemetryFetch('post')}>Fetch post-install site info</button>
                </div>
                {currentRecord.checklist.fetchMessages.post && (() => {
                    const msg = currentRecord.checklist.fetchMessages.post.toLowerCase();
                    
                    // 1. Determine alert color based on message contents
                    let alertClass = "alert blue"; // Default fallback
                    
                    if (msg.includes("fetching") || msg.includes("sending")) {
                        alertClass = "alert amber";
                    } else if (msg.includes("fetched") || msg.includes("successfully")) {
                        alertClass = "alert green";
                    } else if (msg.includes("exception") || msg.includes("abort") || msg.includes("failed")) {
                        alertClass = "alert amber";
                    }

                    // 2. Render the alert element with the dynamically assigned class
                    return (
                        <div className={alertClass}>
                        {currentRecord.checklist.fetchMessages.post}
                        </div>
                    );
                    })()}
                {isPostOpen && (
                  <>
                    <div className="grid2" style={{ marginBottom: "16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                      {/* Map through dynamic fields */}
                      {SERVER_INPUT_FIELDS.map(f => (
                        <div key={f.key} className="field" style={{ display: "flex", flexDirection: "column" }}>
                          <label>{f.label}</label>
                          <input 
                            type="text" 
                            value={currentRecord.checklist.si[f.key] || ''} 
                            onChange={e => handleFieldMutation(f.key, e.target.value)} 
                          />
                        </div>
                      ))}

                      {/* Installed Version Field */}
                      <div className="field" style={{ display: "flex", flexDirection: "column" }}>
                        <label>Installed Version</label>
                        <input 
                          type="text" 
                          value={currentRecord.checklist.si.installedVersion || ''} 
                          onChange={e => handleFieldMutation('installedVersion', e.target.value)} 
                        />
                      </div>

                      {/* Software Package Name Field */}
                      <div className="field" style={{ display: "flex", flexDirection: "column" }}>
                        <label>Software Package Name / Build ID</label>
                        <input 
                          type="text" 
                          value={currentRecord.checklist.si.packageName || ''} 
                          onChange={e => handleFieldMutation('packageName', e.target.value)} 
                        />
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table>
                        <thead>
                          <tr><th>Module</th><th style={{ textAlign: "center", width: "80px" }}>Delivery</th><th style={{ textAlign: "center", width: "75px" }}>Ordered</th><th style={{ textAlign: "center", width: "155px" }}>Installed</th><th style={{ width: "120px" }}>Version</th><th>Notes</th></tr>
                        </thead>
                        <tbody>
                          {MASTER_CHECKLIST_MODULES.map(m => {
                            // Single source of truth for post-install module state.
                            const modRef = currentRecord?.checklist?.snapshots?.post?.modules?.[m.id] || {};
                            const raw = String(modRef.installed ?? '').trim();
                            const resolvedVal = raw === 'N/A'
                              ? 'N/A'
                              : (raw === 'Yes' || raw === 'true') ? 'Yes'
                              : (raw === 'No' || raw === 'false') ? 'No'
                              : '';
                            return (
                              <tr key={m.id}>
                                <td><strong>{m.label}</strong>{m.note && <div className="hint">{m.note}</div>}</td>
                                <td style={{ textAlign: "center" }}><span className="pill">{modRef?.delivery || m.delivery || 'N/A'}</span></td>
                                <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!modRef?.ordered} className="readonly-box" readOnly /></td>
                                <td>
                                  <div className="triOrdered">
                                    {["Yes", "No", "N/A"].map(opt => (
                                      <button
                                        key={opt}
                                        type="button"
                                        className={`${opt === 'Yes' ? 'yes' : opt === 'No' ? 'no' : 'na'} ${resolvedVal === opt ? 'active' : ''}`}
                                        onClick={() => {
                                          const next = resolvedVal === opt ? '' : opt;
                                          mutatePostModule(m.id, 'installed', next);
                                          logChecklistEvent('Set checklist verification step', `"${m.label}" → ${next || 'cleared'}.`);
                                        }}
                                      >
                                        {opt}
                                      </button>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <input type="text" value={modRef?.version || ''} placeholder="e.g. 6.2.2" onChange={e => mutatePostModule(m.id, 'version', e.target.value)} />
                                </td>
                                <td>
                                  <input type="text" value={modRef?.note || ''} placeholder="Notes..." onChange={e => mutatePostModule(m.id, 'note', e.target.value)} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* PERSISTENT RUNTIME ACTION BAR PANEL */}
              <div className="savebar no-print">
                <div><strong>{currentSite.name}</strong><span className="hint" style={{ marginLeft: '12px', color: 'var(--green)', fontWeight: 600 }}>{saveMessage}</span></div>
                <div className="row">
                  <button type="button" className="btn" onClick={() => commitChecklistToDatabase(true)}>Save site checklist</button>
                  <button type="button" className="btn blue" onClick={() => commitChecklistToDatabase(true, 'ready_for_qa')}>Mark ready for QA</button>
                  <button type="button" className="btn green" onClick={handleChecklistVerificationFinalApproval} disabled={approving}>{approving ? "Preparing..." : "Review and Approve"}</button>
                  <button type="button" className="btn" onClick={() => window.print()}>Preview / Print</button>
                  <button type="button" className="btn" onClick={handleNextSiteShift} disabled={currentActiveIndex >= siteListPool.length - 1}>Next site</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}